/**
 * 매뉴얼 문서 피드백("이 문서가 도움이 되었나요?") 저장소.
 *
 * **왜 localStorage인가**: 기존 사용자 피드백 인프라(`POST /api/v1/user-feedback`)는 `JwtAuthGuard` +
 * "회사 소속"을 요구한다(controller가 memberships에서 companyId를 못 찾으면 400). 매뉴얼을 읽는 주체는
 * admin 앱의 **슈퍼관리자**라 회사 소속이 없어 그 경로를 그대로 쓸 수 없고, 서버 스키마 변경은 이번
 * 범위 밖이다. 그래서 응답은 브라우저에만 남기고(+콘솔), 서버 수집은 후속 과제로 둔다.
 *
 * 기록 단위는 문서 하나(`manual/section/slug`) — 어느 문서에 대한 응답인지가 없으면 의미가 없다.
 */

const STORAGE_KEY = 'admin.manual.feedback.v1';

/** 무한정 쌓이지 않게 오래된 응답부터 버린다(브라우저 저장소는 유한하다). */
const MAX_ENTRIES = 300;

export interface ManualFeedbackRecord {
  /** `manual/section/slug` */
  docKey: string;
  helpful: boolean;
  /** ISO 8601(UTC) */
  at: string;
}

export type ManualFeedbackStore = Record<string, ManualFeedbackRecord>;

/** 문서 식별자 — 매뉴얼 경계를 넘지 않도록 매뉴얼 id까지 포함한다. */
export function manualFeedbackKey(manual: string, section: string, slug: string): string {
  return `${manual}/${section}/${slug}`;
}

function safeStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    // 저장소 접근 자체가 막힌 환경(프라이빗 모드 등) — 피드백은 부가 기능이라 조용히 포기한다.
    return null;
  }
}

/** 저장된 응답 전체. 깨진 값은 빈 저장소로 취급한다(문서 렌더를 막지 않는다). */
export function readManualFeedback(): ManualFeedbackStore {
  const storage = safeStorage();
  if (!storage) return {};
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    const store: ManualFeedbackStore = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value !== 'object' || value === null) continue;
      const entry = value as Partial<ManualFeedbackRecord>;
      if (typeof entry.helpful !== 'boolean') continue;
      store[key] = {
        docKey: typeof entry.docKey === 'string' ? entry.docKey : key,
        helpful: entry.helpful,
        at: typeof entry.at === 'string' ? entry.at : '',
      };
    }
    return store;
  } catch {
    return {};
  }
}

/** 이 문서에 이미 응답했는지 — 있으면 다시 묻지 않는다. */
export function getManualFeedback(docKey: string): ManualFeedbackRecord | undefined {
  return readManualFeedback()[docKey];
}

/**
 * 응답 기록. 이미 응답한 문서면 **덮어쓰지 않고** 기존 기록을 그대로 돌려준다
 * (한 문서 한 번 — UI도 이미 응답한 문서엔 질문을 그리지 않는다).
 */
export function recordManualFeedback(docKey: string, helpful: boolean): ManualFeedbackRecord {
  const store = readManualFeedback();
  const existing = store[docKey];
  if (existing) return existing;

  const record: ManualFeedbackRecord = { docKey, helpful, at: new Date().toISOString() };
  store[docKey] = record;

  const storage = safeStorage();
  if (storage) {
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(prune(store)));
    } catch {
      // 용량 초과 등 — 기록 실패가 UI를 막지는 않는다.
    }
  }
  // 서버 수집이 없는 동안의 유일한 관찰 경로.
  console.info('[manual] 문서 피드백', record);
  return record;
}

/** 오래된(`at` 오름차순) 기록부터 버려 상한을 지킨다. */
function prune(store: ManualFeedbackStore): ManualFeedbackStore {
  const entries = Object.entries(store);
  if (entries.length <= MAX_ENTRIES) return store;
  entries.sort(([, a], [, b]) => a.at.localeCompare(b.at));
  return Object.fromEntries(entries.slice(entries.length - MAX_ENTRIES));
}

/** 테스트/운영자 정리용 — 저장된 응답 전체 삭제. */
export function clearManualFeedback(): void {
  const storage = safeStorage();
  if (!storage) return;
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    /* 무시 */
  }
}
